import React, { PureComponent } from 'react';
import { Link } from 'react-router-dom';
import deepcopy from 'deepcopy';
import PropTypes from 'prop-types';
import ShortId from 'shortid';
import ObjectPath from 'object-path';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import Tooltip from '@hobs/components/Tooltip';
import Dialog from '@hobs/components/Dialog';
import Textarea from '@hobs/components/Textarea';
import Dropdown from '@hobs/components/Dropdown';
import Switch from '@hobs/components/Switch';
import Toast from '@hobs/components/Toast';
import Money from '@hobs/components/Money';
import Columns from '@hobs/components/Grid/Columns';
import {
  withDataStateLocalOverrides,
  withGridLocalOverrides,
  onUserResetSettings
} from '@hobs/utils/grid/overrides';
import {
  PAGE_SIZES,
  USER_ROLES,
  INDICATIONS,
  ANNEX_OVERVIEW_EXPORT_FILE_NAME,
  GRID_CUSTOM_FILTER_FIELDS,
  DEFAULT_STATUS_FILTER,
  SERVICE_REQUEST_NAVIGATION_ORIGIN,
  DATE_FORMAT,
  DATETIME_FORMAT
} from '@hobs/utils/constants';
import classnames from 'classnames';
import Icon from '@hobs/components/Icon';
import { Grid, GridColumn } from '@progress/kendo-react-grid';
import {
  ExcelExport,
  ExcelExportColumn
} from '@progress/kendo-react-excel-export';
import { CellDate, CellDateTime } from '@hobs/components/Grid/CellDate';
import GridLoader from '@hobs/components/Grid/GridLoader';
import Status from '@hobs/components/Status';
import { H1 } from '@hobs/components/Headings';
import Indicator from '@hobs/components/Indicator';
import { GhostButton, RaisedButton } from '@hobs/components/Buttons';
import Discussion from '@hobs/containers/Discussion';
import Summary from '@hobs/components/Summary';
import { ServiceRequestNavContext } from '@hobs/components/ServiceRequestNavContext';
import { formatLocal, parse } from '@hobs/utils/formatWithTimeZone';
import './AnnexOverview.scss';
import Quantity from '@hobs/components/Quantity';
import { expandDataItem } from '@hobs/utils/expandItem';
import HnInvoiceCreateDialog from '../HnInvoiceCreateDialog';
import { Icon as BpIcon } from '@blueprintjs/core';

class AnnexOverview extends PureComponent {
  LSdataStateName = `AnnexOverview${this.props.currentExercise.exerciseId}.dataState`;
  LSGridPropsName = `AnnexOverview${this.props.currentExercise.exerciseId}.grid.props`;
  NotificationsQueryFilterName = `NotificationsQueryFilter`;

  handleSelect = dataItem => {
    const selectedRows = {
      ...this.state.selectedRows,
      [dataItem.ServiceRequestId]: !this.state.selectedRows[
        dataItem.ServiceRequestId
      ]
    };

    this.setState({
      selectedRows: selectedRows
    });
  };

  handleSelectAll = () => {
    const selectedServiceRequests = this.getSelectedServiceRequestIds();

    this.setState({
      isAllSelected:
        selectedServiceRequests.length === 0 || !this.state.isAllSelected
          ? !this.state.isAllSelected
          : this.state.isAllSelected,
      selectedRows: {}
    });
  };

  closeDialog = () => {
    this.setState({
      openDialog: false
    });
  };

  toSubcontractorsList = () => {
    const { subcontractors, t } = this.props;
    if (subcontractors && subcontractors.length) {
      const placeholder = (
        <option key={ShortId.generate()} value="">
          {t('Select Subcontractor')}
        </option>
      );

      const options = subcontractors.map(item => {
        return (
          <option key={ShortId.generate()} value={item.id}>
            {item.name}
          </option>
        );
      });

      return [placeholder, options];
    }

    return <option value="">{t('There are no Subcontractors')}</option>;
  };

  openInvoiceDialog = async () => {
    const { t } = this.props;

    const selectedServiceRequests = await this.getServiceRequestIdsForBulkAssign();

    selectedServiceRequests.length > 0
      ? this.setState({
          openInvoiceDialog: true,
          invoiceServiceRequestIds: selectedServiceRequests
        })
      : Toast.warning(t('Please, select service requests first'));
  };

  openDialog = async () => {
    const { t, oData } = this.props;
    const { isAllSelected } = this.state;

    const selectedServiceRequests = this.getSelectedServiceRequestIds();

    const canBulkAssign = await this.canBulkAssign();

    if (canBulkAssign) {
      (!isAllSelected && selectedServiceRequests.length > 0) ||
      (isAllSelected && oData.total !== selectedServiceRequests.length)
        ? this.setState({
            openDialog: true
          })
        : Toast.warning(t('Please, select service requests first'));
    }
  };

  resetAfterSubmission = () => {
    this.closeDialog();
    this.setState({
      selectedRows: {},
      isAllSelected: false
    });
  };

  getSelectedServiceRequestIds = () => {
    const selectedRows = this.state.selectedRows;
    return Object.keys(selectedRows).filter(key => selectedRows[key]);
  };

  async getServiceRequestIdsForBulkAssign() {
    const { getAnnexFilteredServiceRequestIds } = this.props;
    const { isAllSelected } = this.state;

    const selectedServiceRequestIds = this.getSelectedServiceRequestIds();

    if (isAllSelected) {
      const dataState = this.getDataState();
      const filteredServiceRequests = await getAnnexFilteredServiceRequestIds(
        'AnnexServiceRequestQuery',
        { filter: dataState.filter }
      );

      const filteredServiceRequestIds = filteredServiceRequests.data.map(
        request => request.ServiceRequestId
      );

      return filteredServiceRequestIds.filter(requestId => {
        return !selectedServiceRequestIds.includes(requestId);
      });
    }

    return selectedServiceRequestIds;
  }

  canBulkAssign = async () => {
    const { canBulkAssign } = this.props;
    const serviceRequestIds = await this.getServiceRequestIdsForBulkAssign();

    const response = await canBulkAssign({
      serviceRequestIds
    });

    if (response.success) {
      return true;
    }
    if (response.payload && response.payload.message) {
      Toast.warning(response.payload.message);
    }

    return false;
  };

  handleSubmit = async values => {
    const { t, bulkAssign } = this.props;

    const serviceRequestIds = await this.getServiceRequestIdsForBulkAssign();

    const body = {
      serviceRequestIds,
      comment: values.comment ? values.comment : '',
      shouldSendEmail: values.shouldSendEmail ? values.shouldSendEmail : false,
      subcontractorId: values.subcontractorId ? values.subcontractorId : null
    };

    bulkAssign(body).then(response => {
      if (response && response.success === true) {
        Toast.success(t('Subcontractor assigned successfully'));
        this.queryOData();
        this.resetAfterSubmission();
      } else {
        Toast.warning(response.payload.message);
      }
    });
  };

  getShowAllRequestsStatus() {
    const params = new Proxy(new URLSearchParams(window.location.search), {
      get: (searchParams, prop) => searchParams.get(prop)
    });

    return !!params['all-requests'];
  }

  constructor(props) {
    super(props);
    this.validationSchema = Yup.object().shape({
      subcontractorId: Yup.string()
        .required(this.props.t('Subcontractor is required'))
        .nullable()
    });

    this.validationSchemaAppoint = Yup.object().shape({
      unitId: Yup.string()
        .required(this.props.t('Sub-unit is required'))
        .nullable()
    });

    this.exportRef = React.createRef();
    this.grid = React.createRef();

    this.defaultGridColumns = this.getDefaultColumns();

    this.defaultDataState = {
      take: 20,
      skip: 0,
      filter: {
        logic: 'and',
        filters: [DEFAULT_STATUS_FILTER]
      },
      sort: [{ field: 'RequestNumber', dir: 'desc' }]
    };

    const defaultState = {
      XLSoData: [],
      filters: {},
      dataState: withDataStateLocalOverrides(
        deepcopy(this.defaultDataState),
        this.LSdataStateName
      ),
      gridKey: ShortId.generate(),
      customFilters: {},
      columns: withGridLocalOverrides(
        deepcopy(this.defaultGridColumns),
        this.LSGridPropsName
      ),
      openDialog: false,
      selectedRows: {},
      isAllSelected: false,
      previousWindowScrollOffset: null,
      selectedUnit: '',
      showAllRequests: this.getShowAllRequestsStatus()
    };

    this.state = {
      ...defaultState
    };

    this.windowScrollInterval = undefined;
  }

  async componentDidMount() {
    await this.queryOData();
    await this.props.getAnnexes();
    this.props.getAnnexOverviewGrid().then(() => {
      const columns = this.getDefaultColumns();

      const columnsWithLocalOverrides = withGridLocalOverrides(
        deepcopy(columns),
        this.LSGridPropsName
      );

      this.setState({
        columns: {
          ...columnsWithLocalOverrides
        }
      });

      this.props.withVisibleColumns.setColumns(this.state.columns);
      this.props.withVisibleColumns.onUpdate(() => {
        this.setState({
          gridKey: ShortId.generate()
        });
      });
      this.repeatWindowScroll();
    });

    await this.props.bulkAssignForSubmit();

    window.addEventListener('scroll', this.handleScroll);
    window.addEventListener('resize', this.handleScroll);
  }

  componentWillUnmount() {
    this.props.resetOData();
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleScroll);
    if (this.windowScrollInterval) {
      clearInterval(this.windowScrollInterval);
    }
  }

  handleScroll = () => {
    document.getElementsByClassName('k-grid').forEach(grid => {
      const header = grid.getElementsByClassName('k-grid-header');
      if (!header.length) {
        return;
      }

      if (window.pageYOffset >= grid.offsetTop) {
        if (!header[0].classList.contains('fixed-header')) {
          header[0].classList.add('fixed-header');
        }
        header[0].style.maxWidth = `${grid.offsetWidth}px`;
      } else {
        if (header[0].classList.contains('fixed-header')) {
          header[0].classList.remove('fixed-header');
          header[0].style.maxWidth = '';
        }
      }
    });
  };

  dataStateChange = e => {
    this.setState(
      {
        dataState: e.dataState
      },
      () => {
        this.queryOData();
      }
    );
  };

  queryOData = () => {
    const dataState = this.getDataState();

    const { queryOData } = this.props;

    queryOData(
      this.state.showAllRequests
        ? 'HostUnitOverviewQuery'
        : 'AnnexServiceRequestQuery',
      dataState
    );

    localStorage.setItem(
      this.NotificationsQueryFilterName,
      JSON.stringify(this.state.dataState)
    );
  };

  getDataState() {
    const units = this.state.showAllRequests
      ? this.props.huUnits
      : this.props.units;
    const unit = this.flattenUnitsTree(units).find(
      x => x.unitId == this.state.selectedUnit
    );

    let filters = [];

    if (unit) {
      filters.push({
        field: 'AppointedUnitId',
        operator: 'eq',
        value: `<guid>${unit.unitId}</guid>`
      });
    }

    if (Object.keys(this.props.withQuickSearchFilter).length > 0) {
      filters.push(this.props.withQuickSearchFilter);
    }

    if (this.props.withAnnexFilterSelected) {
      filters.push(this.props.withAnnexFilterOdata('AnnexId'));
    }

    if (Object.keys(this.state.customFilters).length > 0) {
      Object.values(this.state.customFilters).map(filter => {
        if (filter) {
          filters.push(filter);
        }
      });
    }

    const dataState = {
      ...this.state.dataState
    };

    if (
      this.state.dataState.filter &&
      this.state.dataState.filter.filters &&
      this.state.dataState.filter.filters.length > 0
    ) {
      filters = filters.concat(this.state.dataState.filter.filters);
    }

    if (filters.length > 0) {
      dataState.filter = {
        logic: 'and',
        filters
      };
    }

    return dataState;
  }

  onQuickSearch = quickSearchValue => {
    const { preFilledQuickSearch, dataState } = this.state;
    this.setState(
      {
        dataState: {
          ...dataState,
          skip: preFilledQuickSearch ? dataState.skip : 0
        },
        quickSearchValue,
        preFilledQuickSearch: null
      },
      () => {
        this.queryOData();
      }
    );
  };

  getFieldValue(field, fallbackValue = '-') {
    if (field === '' || field === null) {
      return fallbackValue;
    }

    return field;
  }

  getSummaryInfo() {
    const { selectedAnnex } = this.state;
    const { currentExercise, t } = this.props;

    const phone = ObjectPath.get(selectedAnnex, 'responsiblePerson.phone')
      ? this.getFieldValue(selectedAnnex.responsiblePerson.phone)
      : this.getFieldValue(
          ObjectPath.get(
            currentExercise,
            'hostInformation.responsiblePersonPhone'
          )
        );
    const name = ObjectPath.get(
      selectedAnnex,
      'responsiblePerson.responsibleName'
    )
      ? this.getFieldValue(selectedAnnex.responsiblePerson.responsibleName)
      : this.getFieldValue(
          ObjectPath.get(
            currentExercise,
            'hostInformation.responsiblePersonName'
          )
        );

    const email = ObjectPath.get(
      selectedAnnex,
      'responsiblePerson.responsibleName'
    )
      ? this.getFieldValue(selectedAnnex.responsiblePerson.email)
      : this.getFieldValue(
          ObjectPath.get(
            currentExercise,
            'hostInformation.responsiblePersonEmail'
          )
        );

    const organization = ObjectPath.get(
      currentExercise,
      'hostInformation.responsiblePersonOrganization'
    )
      ? ObjectPath.get(
          currentExercise,
          'hostInformation.responsiblePersonOrganization'
        ) + t(' responsible')
      : this.getFieldValue(
          ObjectPath.get(
            currentExercise,
            'hostInformation.responsiblePersonOrganization'
          ),
          t('Responsible person')
        );

    return {
      responsiblePerson: {
        phone,
        name,
        email
      },
      requestedQuantity: this.props.oData.total,
      annexName:
        selectedAnnex && selectedAnnex.name ? selectedAnnex.name : null,
      organization,
      sendingNationName: null
    };
  }

  getIndications(indications) {
    return Object.keys(indications).filter(key => {
      return indications[key];
    });
  }

  getIndicationColumnFilter() {
    const filterType = GRID_CUSTOM_FILTER_FIELDS.indicationTypes;
    const { annexOverviewGrid } = this.props;

    if (annexOverviewGrid && annexOverviewGrid[filterType]) {
      return annexOverviewGrid[filterType].map(filterValue => {
        return {
          name: filterValue.title,
          value: true,
          field: `Indications.${filterValue.key}`
        };
      });
    }

    return [];
  }

  getDisplayData() {
    if (this.props.oData.data.length > 0) {
      const displayData = [].concat(this.props.oData.data);
      displayData.map(item => {
        const rowItem = this.state.selectedRows[item.ServiceRequestId];
        item.selectedField = rowItem
          ? !this.state.isAllSelected
          : this.state.isAllSelected;
      });
      displayData.push({
        footer: true,
        FooterTotalActualCost: displayData[0].FooterTotalActualCost,
        FooterTotalEstimatedCostFrom:
          displayData[0].FooterTotalEstimatedCostFrom,
        FooterTotalEstimatedCostTo: displayData[0].FooterTotalEstimatedCostTo,
        FooterTotalEstimatedGrossAmountFrom:
          displayData[0].FooterTotalEstimatedGrossAmountFrom,
        FooterTotalEstimatedGrossAmountTo:
          displayData[0].FooterTotalEstimatedGrossAmountTo,
        FooterTotalActualGrossAmount:
          displayData[0].FooterTotalActualGrossAmount
      });
      return displayData;
    }
    return [];
  }

  exportServiceRequests = async () => {
    await this.getXlsData();
    this.exportRef.current.save();
  };

  onUserSaveSettings = () => {
    localStorage.setItem(
      this.LSdataStateName,
      JSON.stringify(this.state.dataState)
    );
    localStorage.setItem(
      this.LSGridPropsName,
      JSON.stringify({
        columns: this.grid.current.columns
      })
    );
  };

  onUserResetSettings = onUserResetSettings(
    this.LSdataStateName,
    this.LSGridPropsName,
    this.NotificationsQueryFilterName,
    () => {
      const columns = this.getDefaultColumns();

      this.props.withVisibleColumns.setColumns(columns);

      this.setState(
        {
          gridKey: ShortId.generate(),
          columns,
          dataState: this.defaultDataState
        },
        () => {
          this.queryOData();
        }
      );
    }
  );

  getColumns() {
    const {
      withVisibleColumns: { getGridColumns }
    } = this.props;
    return Columns({
      columns: getGridColumns,
      onSave: this.onUserSaveSettings,
      onReset: this.onUserResetSettings,
      dataState: this.state.dataState
    });
  }

  roundNumber(value) {
    return parseFloat(parseFloat(value).toFixed(2));
  }

  async getXlsData() {
    const { t } = this.props;
    const dataState = this.getDataState();
    delete dataState.take;
    dataState.skip = 0;
    const XLSoData = await this.props.queryXLSOData(
      'AnnexServiceRequestQuery',
      dataState
    );

    const data = XLSoData.data.map(item => {
      const indications = this.getIndications(item.Indications);
      item.Indications = indications
        .map(indication => INDICATIONS[indication])
        .join('; ');
      item.AdditionalInfo = item.AdditionalInfo
        ? item.AdditionalInfo
        : t('False');
      item.IsCalculatedByDays = item.IsCalculatedByDays ? t('Yes') : t('No');
      item.IsDeliveryFinalized = item.IsDeliveryFinalized ? t('Yes') : t('No');
      item.StatusId = item.StatusGridName;
      item.IsPaid = item.IsPaid ? t('Yes') : t('No');
      item.DateFrom = parse(item.DateFrom);
      item.DateTo = parse(item.DateTo);
      item.CreationDate = parse(item.CreationDate);
      item.EstimatedGrossAmountFrom = item.EstimatedGrossAmountFrom
        ? this.roundNumber(item.EstimatedGrossAmountFrom)
        : item.EstimatedGrossAmountFrom;
      item.EstimatedGrossAmountTo = item.EstimatedGrossAmountTo
        ? this.roundNumber(item.EstimatedGrossAmountTo)
        : item.EstimatedGrossAmountTo;
      item.EstimatedTotalCostFrom = item.EstimatedTotalCostFrom
        ? this.roundNumber(item.EstimatedTotalCostFrom)
        : item.EstimatedTotalCostFrom;
      item.EstimatedTotalCostTo = item.EstimatedTotalCostTo
        ? this.roundNumber(item.EstimatedTotalCostTo)
        : item.EstimatedTotalCostTo;
      item.TotalCost = item.TotalCost
        ? this.roundNumber(item.TotalCost)
        : item.TotalCost;
      item.TotalGrossCost = item.TotalGrossCost
        ? this.roundNumber(item.TotalGrossCost)
        : item.TotalGrossCost;
      item.ActualGrossAmount = item.ActualGrossAmount
        ? this.roundNumber(item.ActualGrossAmount)
        : item.ActualGrossAmount;
      item.IsPaid = item.IsPaid ? t('Yes') : t('No');
      item.PendingFlag = item.PendingFlag ? t('Yes') : t('No');
      item.PendingDate = item.PendingDate ? parse(item.PendingDate) : null;
      return item;
    });

    this.setState({
      XLSoData: data
    });
  }

  getXlsTotals = () => {
    const { XLSoData } = this.state;
    return {
      FooterTotalActualCost: XLSoData[0].FooterTotalActualCost
        ? this.roundNumber(XLSoData[0].FooterTotalActualCost)
        : XLSoData[0].FooterTotalActualCost,
      FooterTotalEstimatedCostFrom: XLSoData[0].FooterTotalEstimatedCostFrom
        ? this.roundNumber(XLSoData[0].FooterTotalEstimatedCostFrom)
        : XLSoData[0].FooterTotalEstimatedCostFrom,
      FooterTotalEstimatedCostTo: XLSoData[0].FooterTotalEstimatedCostTo
        ? this.roundNumber(XLSoData[0].FooterTotalEstimatedCostTo)
        : XLSoData[0].FooterTotalEstimatedCostTo,
      FooterTotalEstimatedGrossAmountFrom: XLSoData[0]
        .FooterTotalEstimatedGrossAmountFrom
        ? this.roundNumber(XLSoData[0].FooterTotalEstimatedGrossAmountFrom)
        : XLSoData[0].FooterTotalEstimatedGrossAmountFrom,
      FooterTotalEstimatedGrossAmountTo: XLSoData[0]
        .FooterTotalEstimatedGrossAmountTo
        ? this.roundNumber(XLSoData[0].FooterTotalEstimatedGrossAmountTo)
        : XLSoData[0].FooterTotalEstimatedGrossAmountTo,
      FooterTotalActualGrossAmount: XLSoData[0].FooterTotalActualGrossAmount
        ? this.roundNumber(XLSoData[0].FooterTotalActualGrossAmount)
        : XLSoData[0].FooterTotalActualGrossAmount
    };
  };

  getColumnFilterValues(gridFilterFieldName, field) {
    const { annexOverviewGrid } = this.props;

    if (annexOverviewGrid && annexOverviewGrid[gridFilterFieldName]) {
      return annexOverviewGrid[gridFilterFieldName].map(filterValue => {
        const filter = {
          name: filterValue.title,
          value: `<guid>${filterValue.id}</guid>`
        };

        if (field) {
          filter.field = field;
        }
        return filter;
      });
    }

    return [];
  }

  getColumnFilterValuesAsString(gridFilterFieldName, field) {
    const { annexOverviewGrid } = this.props;

    if (annexOverviewGrid && annexOverviewGrid[gridFilterFieldName]) {
      return annexOverviewGrid[gridFilterFieldName].map(filterValue => {
        const filter = {
          name: filterValue.title,
          value: filterValue.id
        };

        if (field) {
          filter.field = field;
        }
        return filter;
      });
    }

    return [];
  }

  getColumnFilterValuesAsStringNoId(gridFilterFieldName, field) {
    const { annexOverviewGrid } = this.props;

    if (annexOverviewGrid && annexOverviewGrid[gridFilterFieldName]) {
      return annexOverviewGrid[gridFilterFieldName].map(filterValue => {
        const filter = {
          name: filterValue.title,
          value: filterValue.title
        };

        if (field) {
          filter.field = field;
        }
        return filter;
      });
    }

    return [];
  }

  getColumnFilterByNameValues(gridFilterFieldName) {
    const { annexOverviewGrid } = this.props;

    if (annexOverviewGrid && annexOverviewGrid[gridFilterFieldName]) {
      return annexOverviewGrid[gridFilterFieldName].map(filterValue => {
        const value = filterValue.title ? filterValue.title : filterValue;

        return {
          name: value,
          value: value
        };
      });
    }

    return [];
  }

  getDiscussionPath(srId) {
    const { match } = this.props;
    return `${match.url}/discussion/${srId}`;
  }

  getDefaultColumns() {
    const columns = this._getDefaultColumns();

    delete columns.TotalCost;
    delete columns.TotalGrossCost;

    return columns;
  }

  _getDefaultColumns() {
    const { t, hasUserPermission, history } = this.props;

    const columns = {
      Indications: {
        title: t('Indications'),
        columnMenu: true,
        default: true,
        sortable: false,
        visible: true,
        width: '46px',
        headerCell: () => (
          <div>
            <span className="indicator-bubble"></span>
          </div>
        ),
        cell: ({ dataItem }) => {
          const indications = this.getIndications(dataItem.Indications);
          const hasIndication = dataItem.HasIndication;
          return (
            <td valign="middle">
              {indications.length > 0 && hasIndication && (
                <Indicator
                  message={indications.map(indication => (
                    <div key={ShortId.generate()}>
                      {INDICATIONS[indication]}
                    </div>
                  ))}
                  key={ShortId.generate()}
                />
              )}
            </td>
          );
        },
        customFilter: {
          data: [
            {
              name: t('Select all'),
              default: true,
              value: ''
            },
            ...this.getIndicationColumnFilter()
          ]
        }
      },
      AdditionalInfo: {
        title: t('Additional Info'),
        default: true,
        visible: true,
        width: '45px',
        cell: ({ dataItem }) => (
          <td>
            {dataItem.IsActionRequiredFromHn && (
              <Tooltip
                tooltip={`Last edit by: ${
                  dataItem.SnAuditInfoName
                } ${formatLocal(dataItem.SnAuditInfoDate, DATETIME_FORMAT)}`}
                position="top"
              >
                <BpIcon icon="warning-sign" color="#fe9904" />
              </Tooltip>
            )}
            {dataItem.AdditionalInfo && (
              <ServiceRequestNavContext.Consumer>
                {nav => (
                  <Link
                    to={`/service-request/${dataItem.PartyId}/${dataItem.ServiceRequestId}/change-history`}
                    onClick={() => {
                      nav.setPreviousPageHistory(history);
                      nav.setPreviousGridPosition(
                        this.state.dataState,
                        window.pageYOffset,
                        this.state.selectedAnnex,
                        this.state.quickSearchValue,
                        SERVICE_REQUEST_NAVIGATION_ORIGIN.AnnexOverview,
                        this.state.selectedUnit
                      );
                      nav.setServiceRequestId(dataItem.ServiceRequestId);
                    }}
                  >
                    <Tooltip
                      tooltip={
                        dataItem.LatestTicketAction &&
                        `${dataItem.LatestTicketAction}${formatLocal(
                          dataItem.LatestTicketActionDate,
                          DATE_FORMAT
                        )})`
                      }
                      position="top"
                    >
                      <Icon
                        className={classnames(
                          'info-indication',
                          {
                            active: dataItem.AdditionalInfoActive
                          },
                          {
                            rejected: dataItem.AdditionalInfoRejected
                          }
                        )}
                        icon="InfoSmall"
                      />
                    </Tooltip>
                  </Link>
                )}
              </ServiceRequestNavContext.Consumer>
            )}
          </td>
        )
      },
      StatusId: {
        title: t('Status'),
        default: true,
        visible: true,
        width: 103,
        cell: ({ dataItem }) => (
          <td colSpan="1">
            <Status
              type={parseInt(dataItem.StatusId, 10)}
              name={dataItem.StatusGridName}
            />
          </td>
        ),
        columnMenu: true,
        customFilter: {
          data: [
            {
              name: t('Select all'),
              default: true,
              value: ''
            },
            ...this.getColumnFilterValues(GRID_CUSTOM_FILTER_FIELDS.statuses)
          ]
        },
        xlsField: 'StatusGridName'
      },
      IsFullyAssignedName: {
        title: t('Assigned'),
        default: false,
        visible: false,
        columnMenu: true,
        customFilter: {
          data: [
            {
              name: t('Select all'),
              default: true,
              value: ''
            },
            ...this.getColumnFilterValues(
              GRID_CUSTOM_FILTER_FIELDS.fullyAssigned,
              'IsFullyAssigned'
            )
          ]
        }
      },
      SendingNationId: {
        title: t('SN'),
        default: true,
        visible: true,
        columnMenu: true,
        customFilter: {
          data: [
            {
              name: t('Select all'),
              default: true,
              value: ''
            },
            ...this.getColumnFilterValues(
              GRID_CUSTOM_FILTER_FIELDS.sendingNations
            )
          ]
        },
        cell: ({ dataItem }) => {
          return <td>{dataItem.SendingNation}</td>;
        },
        xlsField: 'SendingNation'
      },
      RequestNumber: {
        title: t('Req.'),
        columnMenu: true,
        default: true,
        visible: true,
        width: '84px',
        cell: ({ dataItem }) => (
          <td>
            <ServiceRequestNavContext.Consumer>
              {nav => (
                <Link
                  to={`/request-information/${dataItem.ServiceRequestId}`}
                  onClick={() => {
                    nav.setPreviousPageHistory(history);

                    nav.setPreviousGridPosition(
                      this.state.dataState,
                      window.pageYOffset,
                      this.state.selectedAnnex,
                      this.state.quickSearchValue,
                      SERVICE_REQUEST_NAVIGATION_ORIGIN.AnnexOverview,
                      this.state.selectedUnit
                    );
                  }}
                >
                  {dataItem.RequestNumber}
                </Link>
              )}
            </ServiceRequestNavContext.Consumer>
          </td>
        )
      },
      CreationDate: {
        title: t('Creation date'),
        columnMenu: true,
        default: false,
        visible: false,
        filter: 'date',
        cell: CellDateTime
      },
      LastChangeDate: {
        title: t('Last change'),
        columnMenu: true,
        default: false,
        visible: false,
        filter: 'date',
        cell: CellDateTime
      },
      ServiceCode: {
        title: t('Service code'),
        columnMenu: true,
        default: true,
        visible: true
      },
      ServiceName: {
        title: t('Service name'),
        columnMenu: true,
        default: true,
        visible: true
      },
      ServiceDescription: {
        title: t('Description'),
        columnMenu: true,
        default: false,
        visible: false
      },
      Group: {
        title: t('Group'),
        columnMenu: true,
        default: true,
        visible: true,
        customFilter: {
          filter: 'contains',
          data: [
            {
              name: t('Select all'),
              default: true,
              value: ''
            },
            ...this.getColumnFilterValuesAsStringNoId(
              GRID_CUSTOM_FILTER_FIELDS.groups,
              'GroupNames'
            )
          ]
        }
      },
      PendingFlag: {
        title: t('Pending Flag'),
        columnMenu: true,
        default: false,
        visible: false,
        cell: ({ dataItem }) => {
          return <td>{dataItem.PendingFlag ? t('Yes') : t('No')}</td>;
        },
        customFilter: {
          data: [
            {
              name: t('Select all'),
              default: true,
              value: ''
            },
            {
              name: t('Yes'),
              value: true
            },
            {
              name: t('No'),
              value: false
            }
          ]
        }
      },
      PendingDate: {
        title: t('Pending Date'),
        columnMenu: true,
        default: false,
        visible: false,
        filter: 'date',
        cell: CellDate,
        width: '78px'
      },
      PendingComment: {
        title: t('Pending Comment'),
        columnMenu: true,
        default: false,
        visible: false
      },
      AnnexId: {
        title: t('Annex'),
        default: false,
        visible: false,
        columnMenu: true,
        customFilter: {
          data: [
            {
              name: t('Select all'),
              default: true,
              value: ''
            },
            ...this.getColumnFilterValues(GRID_CUSTOM_FILTER_FIELDS.annexes)
          ]
        },
        cell: ({ dataItem }) => {
          return <td>{dataItem.AnnexName}</td>;
        },
        xlsField: 'AnnexName'
      },
      RequestedQuantity: {
        title: t('Req. q-ty'),
        filter: 'numeric',
        columnMenu: true,
        default: true,
        visible: true,
        cell: ({ dataItem }) => {
          return (
            <td className="custom-cell" onClick={expandDataItem}>
              <Quantity amount={dataItem.RequestedQuantity} />
            </td>
          );
        }
      },
      IsCalculatedByDays: {
        title: t('Calculated by days'),
        default: true,
        visible: true,
        columnMenu: true,
        cell: ({ dataItem }) => {
          return <td>{dataItem.IsCalculatedByDays ? t('Yes') : t('No')}</td>;
        },
        customFilter: {
          data: [
            {
              name: t('Select all'),
              default: true,
              value: ''
            },
            {
              name: t('Yes'),
              value: true
            },
            {
              name: t('No'),
              value: false
            }
          ]
        }
      },
      TotalQuantity: {
        title: t('Total q-ty'),
        filter: 'numeric',
        columnMenu: true,
        default: true,
        visible: true,
        cell: ({ dataItem }) => {
          return (
            <td className="custom-cell" onClick={expandDataItem}>
              <Quantity amount={dataItem.TotalQuantity} />
            </td>
          );
        }
      },
      UnallocatedQuantity: {
        title: t('Unall. q-ty'),
        filter: 'numeric',
        columnMenu: true,
        default: false,
        visible: false,
        cell: ({ dataItem }) => {
          return (
            <td className="custom-cell" onClick={expandDataItem}>
              <Quantity amount={dataItem.UnallocatedQuantity} />
            </td>
          );
        }
      },
      DeliveryQuantity: {
        title: t('Delivery q-ty'),
        filter: 'numeric',
        columnMenu: true,
        default: false,
        visible: false,
        cell: ({ dataItem }) => {
          return (
            <td className="custom-cell" onClick={expandDataItem}>
              <Quantity amount={dataItem.DeliveryQuantity} />
            </td>
          );
        }
      },
      DeliveryComments: {
        title: t('Delivered comment'),
        columnMenu: true,
        default: false,
        visible: false
      },
      IsDeliveryFinalized: {
        title: t('Delivery finalized'),
        default: false,
        visible: false,
        columnMenu: true,
        cell: ({ dataItem }) => {
          return <td>{dataItem.IsDeliveryFinalized ? t('Yes') : t('No')}</td>;
        },
        customFilter: {
          data: [
            {
              name: t('Select all'),
              default: true,
              value: ''
            },
            {
              name: t('Yes'),
              value: true
            },
            {
              name: t('No'),
              value: false
            }
          ]
        }
      },
      UnitOfMeasureId: {
        title: t('UoM'),
        default: true,
        visible: true,
        columnMenu: true,
        customFilter: {
          data: [
            {
              name: t('Select all'),
              default: true,
              value: ''
            },
            ...this.getColumnFilterValues(
              GRID_CUSTOM_FILTER_FIELDS.unitsOfMeasure
            )
          ]
        },
        cell: ({ dataItem }) => {
          return <td>{dataItem.UnitOfMeasure}</td>;
        },
        xlsField: 'UnitOfMeasure'
      },
      Suppliers: {
        title: t('Suppliers'),
        columnMenu: true,
        default: false,
        visible: false,
        customFilter: {
          filter: 'contains',
          data: [
            {
              name: t('Select all'),
              default: true,
              value: ''
            },
            ...this.getColumnFilterValuesAsString(
              GRID_CUSTOM_FILTER_FIELDS.Suppliers,
              'SuppliersIds'
            )
          ]
        }
      },
      Unit: {
        title: t('Unit'),
        columnMenu: true,
        default: true,
        visible: true
      },
      AppointedUnit: {
        title: t('Appointed to'),
        columnMenu: true,
        default: true,
        visible: true,
        customFilter: {
          filter: 'contains',
          data: [
            {
              name: t('Select all'),
              default: true,
              value: ''
            },
            ...this.getColumnFilterValuesAsString(
              GRID_CUSTOM_FILTER_FIELDS.appointedUnits,
              'AppointedUnitIds'
            )
          ]
        }
      },
      AppointedUnitId: {
        default: false,
        visible: false
      },
      Location: {
        title: t('Location'),
        columnMenu: true,
        default: true,
        visible: true,
        customFilter: {
          filter: 'contains',
          data: [
            {
              name: t('Select all'),
              default: true,
              value: ''
            },
            ...this.getColumnFilterValuesAsStringNoId(
              GRID_CUSTOM_FILTER_FIELDS.locations,
              'LocationFromNames'
            )
          ]
        }
      },
      LocationComment: {
        title: t('Location comment'),
        columnMenu: true,
        default: false,
        visible: false
      },
      LocationTo: {
        title: t('Delivery Location'),
        columnMenu: true,
        default: true,
        visible: false,
        customFilter: {
          filter: 'contains',
          data: [
            {
              name: t('Select all'),
              default: true,
              value: ''
            },
            ...this.getColumnFilterValuesAsStringNoId(
              GRID_CUSTOM_FILTER_FIELDS.locationsTo,
              'LocationToNames'
            )
          ]
        }
      },
      LocationCommentTo: {
        title: t('Delivery location comment'),
        columnMenu: true,
        default: false,
        visible: false
      },
      RegionId: {
        title: t('Region'),
        columnMenu: true,
        default: true,
        visible: true,
        customFilter: {
          filter: 'contains',
          data: [
            {
              name: t('Select all'),
              default: true,
              value: ''
            },
            ...this.getColumnFilterValuesAsString(
              GRID_CUSTOM_FILTER_FIELDS.regions,
              'RegionIds'
            )
          ]
        },
        cell: ({ dataItem }) => {
          return <td>{dataItem.Region}</td>;
        },
        xlsField: 'Region'
      },
      Site: {
        title: t('Site'),
        columnMenu: true,
        default: false,
        visible: false
      },
      Latitude: {
        title: t('Latitude'),
        columnMenu: true,
        default: false,
        visible: false
      },
      Longitude: {
        title: t('Longitude'),
        columnMenu: true,
        default: false,
        visible: false
      },
      DateFrom: {
        title: t('From date'),
        columnMenu: true,
        default: true,
        visible: true,
        filter: 'date',
        cell: CellDate,
        width: '78px'
      },
      DateTo: {
        title: t('To date'),
        columnMenu: true,
        default: true,
        visible: true,
        filter: 'date',
        cell: CellDate,
        width: '78px'
      },
      Days: {
        title: t('Days'),
        default: true,
        sortable: true,
        visible: true,
        width: '48px',
        columnMenu: true,
        filter: 'numeric'
      },
      UnitPrice: {
        title: t('Unit price'),
        columnMenu: true,
        filter: 'numeric',
        default: false,
        visible: false,
        xls: false,
        cell: ({ dataItem }) => {
          return (
            <td className="custom-cell" onClick={expandDataItem}>
              <Money amount={dataItem.UnitPrice} />
            </td>
          );
        }
      },
      Comment: {
        title: t('Comment'),
        columnMenu: true,
        default: false,
        visible: false
      },
      ResponseComments: {
        title: t('Response comment'),
        columnMenu: true,
        default: false,
        visible: false
      },
      InvoiceNumber: {
        title: t('Invoice ref.'),
        columnMenu: true,
        default: false,
        visible: false,
        cell: ({ dataItem }) => {
          return (
            <td>
              {dataItem.InvoiceReferenceId && (
                <>
                  <Link
                    to={`/annex-overview/invoice/${dataItem.InvoiceReferenceId}`}
                  >
                    {dataItem.InvoiceNumber}
                  </Link>
                </>
              )}
            </td>
          );
        }
      },
      PurchaseOrderNumber: {
        title: t('PO No.'),
        filter: 'numeric',
        columnMenu: true,
        default: false,
        visible: false
      },
      PositionNumber: {
        title: t('Pos. No.'),
        filter: 'numeric',
        columnMenu: true,
        default: false,
        visible: false
      },
      RateFrom: {
        title: t('Rate from'),
        filter: 'numeric',
        columnMenu: true,
        default: false,
        visible: false,
        cell: ({ dataItem }) => {
          return (
            <td className="custom-cell" onClick={expandDataItem}>
              <Money amount={dataItem.RateFrom} />
            </td>
          );
        }
      },
      RateTo: {
        title: t('Rate to'),
        filter: 'numeric',
        columnMenu: true,
        default: false,
        visible: false,
        cell: ({ dataItem }) => {
          return (
            <td className="custom-cell" onClick={expandDataItem}>
              <Money amount={dataItem.RateTo} />
            </td>
          );
        }
      },
      EstimatedTotalCost: {
        title: t('Total cost'),
        filter: 'numeric',
        default: true,
        visible: true,
        sortable: false,
        xls: false,
        cell: ({ dataItem }) => {
          return (
            <td className="custom-cell" onClick={expandDataItem}>
              <Money amount={dataItem.EstimatedTotalCostFrom} />
              {dataItem.EstimatedTotalCostFrom !==
              dataItem.EstimatedTotalCostTo ? (
                <>
                  {' '}
                  - <Money amount={dataItem.EstimatedTotalCostTo} />
                </>
              ) : (
                ''
              )}
            </td>
          );
        }
      },
      EstimatedGrossAmount: {
        title: t('Total gross amount'),
        default: false,
        visible: false,
        xls: false,
        cell: ({ dataItem }) => {
          return (
            <td className="custom-cell" onClick={expandDataItem}>
              <Money amount={dataItem.EstimatedGrossAmountFrom} />
              {dataItem.EstimatedGrossAmountFrom !==
              dataItem.EstimatedGrossAmountTo ? (
                <>
                  {' '}
                  - <Money amount={dataItem.EstimatedGrossAmountTo} />
                </>
              ) : (
                ''
              )}
            </td>
          );
        }
      },
      TotalCost: {
        //Total cost for SP
        title: t('Total cost'),
        filter: 'numeric',
        default: true,
        visible: true,
        sortable: false,
        xls: false,
        cell: ({ dataItem }) => {
          return (
            <td className="custom-cell" onClick={expandDataItem}>
              <Money amount={dataItem.TotalCost} />
            </td>
          );
        }
      },
      TotalGrossCost: {
        //Total gross for SP
        title: t('Total gross amount'),
        default: false,
        visible: false,
        xls: false,
        cell: ({ dataItem }) => {
          return (
            <td className="custom-cell" onClick={expandDataItem}>
              <Money amount={dataItem.TotalGrossCost} />
            </td>
          );
        }
      },
      ActualCost: {
        title: t('Actual cost'),
        columnMenu: true,
        filter: 'numeric',
        default: false,
        visible: false,
        xls: false,
        cell: ({ dataItem }) => {
          return (
            <td className="custom-cell" onClick={expandDataItem}>
              <Money amount={dataItem.ActualCost} />
            </td>
          );
        }
      },
      ActualGrossAmount: {
        title: t('Actual gross amount'),
        filter: 'numeric',
        columnMenu: true,
        default: false,
        visible: false,
        xls: false,
        cell: ({ dataItem }) => {
          return (
            <td className="custom-cell" onClick={expandDataItem}>
              <Money amount={dataItem.ActualGrossAmount} />
            </td>
          );
        }
      },
      IsPaid: {
        title: t('Is Paid'),
        default: false,
        columnMenu: true,
        visible: false,
        cell: ({ dataItem }) => {
          return <td>{dataItem.IsPaid ? t('Yes') : t('No')}</td>;
        },
        customFilter: {
          data: [
            {
              name: t('Select all'),
              default: true,
              value: ''
            },
            {
              name: t('Yes'),
              value: true
            },
            {
              name: t('No'),
              value: false
            }
          ]
        }
      },
      IsSharedCost: {
        title: t('Is Shared Cost'),
        default: false,
        columnMenu: true,
        visible: false,
        cell: ({ dataItem }) => {
          return <td>{dataItem.IsSharedCost ? t('Yes') : t('No')}</td>;
        },
        customFilter: {
          data: [
            {
              name: t('Select all'),
              default: true,
              value: ''
            },
            {
              name: t('Yes'),
              value: true
            },
            {
              name: t('No'),
              value: false
            }
          ]
        }
      },
      ActivityName: {
        title: t('Activity'),
        columnMenu: true,
        default: false,
        visible: false
      },
      HasAttachments: {
        minResizableWidth: '33px',
        title: t('Attachment'),
        cell: ({ dataItem }) => (
          <td colSpan="1">
            {dataItem.HasAttachments && (
              <Icon icon="attachment" className="service-requests-view--icon" />
            )}
          </td>
        ),
        headerCell: () => (
          <Icon icon="attachment" className="service-requests-view--icon" />
        ),
        default: false,
        visible: false,
        xls: false,
        customFilter: {
          data: [
            {
              name: t('Select all'),
              default: true,
              value: ''
            },
            {
              name: t('Yes'),
              value: true
            },
            {
              name: t('No'),
              value: false
            }
          ]
        },
        columnMenu: true
      },
      DiscussionStatus: {
        title: t('Disc.'),
        default: true,
        columnMenu: true,
        visible: true,
        xls: false,
        width: '45px',
        cell: ({ dataItem }) => {
          return (
            <td>
              <Link to={this.getDiscussionPath(dataItem.ServiceRequestId)}>
                <Discussion
                  serviceRequestId={dataItem.ServiceRequestId}
                  status={dataItem.DiscussionStatus}
                  iconOnly={true}
                  onDiscussionClose={this.queryOData}
                  initials={t('SN')}
                  action={'sn-discussion'}
                  isSeen={dataItem.IsLastDiscussionSeen}
                />
              </Link>
            </td>
          );
        },
        customFilter: {
          data: [
            {
              name: t('Select all'),
              default: true,
              value: ''
            },
            {
              name: t('Unread messages'),
              value: 1
            },
            {
              name: t('Read messages'),
              value: 2
            },
            {
              name: t('No messages'),
              value: 3
            }
          ]
        }
      },
      IsExcludedFromFsorConfirmation: {
        title: t('Is Excluded From Fsor Confirmation'),
        default: false,
        columnMenu: true,
        visible: false,
        cell: ({ dataItem }) => {
          return (
            <td>
              {dataItem.IsExcludedFromFsorConfirmation ? t('Yes') : t('No')}
            </td>
          );
        },
        customFilter: {
          data: [
            {
              name: t('Select all'),
              default: true,
              value: ''
            },
            {
              name: t('Yes'),
              value: true
            },
            {
              name: t('No'),
              value: false
            }
          ]
        }
      },
      IsEarlyConfirmationRequired: {
        title: t('Is Early Confirmation Required'),
        default: false,
        columnMenu: true,
        visible: false,
        cell: ({ dataItem }) => {
          return (
            <td>{dataItem.IsEarlyConfirmationRequired ? t('Yes') : t('No')}</td>
          );
        },
        customFilter: {
          data: [
            {
              name: t('Select all'),
              default: true,
              value: ''
            },
            {
              name: t('Yes'),
              value: true
            },
            {
              name: t('No'),
              value: false
            }
          ]
        }
      },
      IsSupplierDeliveryFinalized: {
        title: t('Is Supplier Del. Finalized'),
        default: false,
        columnMenu: true,
        visible: false,
        cell: ({ dataItem }) => {
          return (
            <td>
              {dataItem.IsSupplierDeliveryFinalized === 1
                ? t('Yes')
                : dataItem.IsSupplierDeliveryFinalized === 0
                ? t('No')
                : t('Partial')}
            </td>
          );
        },
        customFilter: {
          data: [
            {
              name: t('Select all'),
              default: true,
              value: ''
            },
            {
              name: t('Yes'),
              value: 1
            },
            {
              name: t('No'),
              value: 0
            },
            {
              name: t('Partial'),
              value: 2
            }
          ]
        }
      }
    };

    return columns;
  }

  isInvoiceDialogOpen = () => {
    const { match } = this.props;
    return match.params.action === 'invoice';
  };

  closeInvoiceDialog = () => {
    const { history } = this.props;
    history.push('/annex-overview');
  };

  navigateToSavedLocation(nav) {
    if (
      nav.previousGridPosition.windowScrollOffset &&
      nav.previousGridPosition.navigationOrigin ===
        SERVICE_REQUEST_NAVIGATION_ORIGIN.AnnexOverview &&
      this.state.previousWindowScrollOffset !==
        nav.previousGridPosition.windowScrollOffset
    ) {
      this.setState({
        previousWindowScrollOffset: nav.previousGridPosition.windowScrollOffset
      });
      nav.clearPreviousPosition();
    }
  }

  // Consider moving this to utils or separate file to reuse it
  setSavedDataState(nav) {
    const newState = {};
    if (
      nav.previousGridPosition.dataState &&
      nav.previousGridPosition.navigationOrigin !==
        SERVICE_REQUEST_NAVIGATION_ORIGIN.AnnexOverview
    ) {
      nav.clearPreviousPosition();
    }

    if (
      nav.previousGridPosition.dataState &&
      nav.previousGridPosition.navigationOrigin ===
        SERVICE_REQUEST_NAVIGATION_ORIGIN.AnnexOverview &&
      JSON.stringify(nav.previousGridPosition.dataState) !==
        JSON.stringify(this.state.dataState)
    ) {
      Object.assign(newState, {
        dataState: nav.previousGridPosition.dataState,
        previousWindowScrollOffset: nav.previousGridPosition.windowScrollOffset
      });
    }

    if (
      nav.previousGridPosition.selectedAnnex &&
      JSON.stringify(nav.previousGridPosition.selectedAnnex) !==
        JSON.stringify(this.state.preselectedAnnex)
    ) {
      Object.assign(newState, {
        preselectedAnnex: nav.previousGridPosition.selectedAnnex
      });
      nav.clearSelectedAnnex();
    }
    if (
      nav.previousGridPosition.savedQuickSearch &&
      JSON.stringify(nav.previousGridPosition.savedQuickSearch) !==
        JSON.stringify(this.state.preFilledQuickSearch)
    ) {
      Object.assign(newState, {
        preFilledQuickSearch: nav.previousGridPosition.savedQuickSearch
      });
      nav.clearSavedQuickSearch();
    }
    if (
      nav.previousGridPosition.selectedUnit &&
      nav.previousGridPosition.navigationOrigin ===
        SERVICE_REQUEST_NAVIGATION_ORIGIN.AnnexOverview
    ) {
      Object.assign(newState, {
        selectedUnit: nav.previousGridPosition.selectedUnit
      });
      nav.clearSelectedUnit();
    }

    if (JSON.stringify(newState) !== JSON.stringify({})) {
      this.setState(newState, () => {
        this.queryOData();
      });
    }

    if (nav.previousGridPosition && nav.previousGridPosition.dataState) {
      nav.clearSavedDataState();
    }
  }

  clearSavedOffset() {
    if (
      this.state.previousWindowScrollOffset &&
      window.scrollY === this.state.previousWindowScrollOffset
    ) {
      this.setState({
        previousWindowScrollOffset: null
      });
    }
  }

  scrollWindow() {
    this.state.previousWindowScrollOffset &&
      window.scrollTo(0, this.state.previousWindowScrollOffset);
  }

  assignDialog() {
    const { t } = this.props;
    return (
      <Dialog
        title={t('Bulk Action')}
        isOpen={this.state.openDialog}
        onClose={this.closeDialog}
      >
        <div className="annex-overview--dialog">
          <Formik
            validationSchema={this.validationSchema}
            onSubmit={this.handleSubmit}
            enableReinitialize={true}
            initialValues={{ subcontractorId: null }}
          >
            {({ setFieldValue, isSubmitting }) => (
              <Form>
                <Field
                  name="subcontractorId"
                  component={Dropdown}
                  label={t('Subcontractor')}
                >
                  {this.toSubcontractorsList()}
                </Field>
                <Field
                  name="comment"
                  component={Textarea}
                  placeholder="Enter comment"
                  label={t('Comment')}
                />
                <Field
                  name="shouldSendEmail"
                  render={({ field }) => {
                    return (
                      <div className="switch-wrapper">
                        <Switch
                          alignIndicator="right"
                          name="shouldSendEmail"
                          setFieldValue={(name, value) => {
                            setFieldValue('shouldSendEmail', value);
                          }}
                          {...field}
                        >
                          {`${t('Send Email')}:`}
                        </Switch>
                        <span className="send-value">
                          {field.value ? t('Yes') : t('No')}
                        </span>
                      </div>
                    );
                  }}
                />
                <RaisedButton type="submit" disabled={isSubmitting}>
                  {t('Assign')}
                </RaisedButton>
              </Form>
            )}
          </Formik>
        </div>
      </Dialog>
    );
  }

  UnitsFilter() {
    return;
  }

  onHuChange = value => {
    const { match, history } = this.props;

    const params = {
      pathname: match.url
    };

    if (value) {
      params.search = `?${new URLSearchParams({
        'all-requests': value
      }).toString()}`;
    }

    history.push(params);

    this.setState(
      {
        showAllRequests: value
      },
      () => {
        this.queryOData();
      }
    );
  };

  HUOverview() {
    return;
  }

  renderInvoiceCreateDialog() {
    return (
      <HnInvoiceCreateDialog
        reload={() => {
          this.queryOData();
          this.resetAfterSubmission();
        }}
        serviceRequestIds={this.state.invoiceServiceRequestIds}
        isDialogOpen={this.state.openInvoiceDialog}
        closeDialog={() => {
          this.setState({
            openInvoiceDialog: false,
            invoiceServiceRequestIds: null
          });
        }}
      />
    );
  }

  flattenUnitsTree(array, level = 0) {
    if (!Array.isArray(array)) {
      return [];
    }

    return array.reduce((result, item) => {
      result.push({
        unitName: `${'\xa0\xa0\xa0'.repeat(level)}${item.title}`,
        unitId: item.unitId,
        disabled: item.disabled,
        units: item.units
      });

      if (Array.isArray(item.units)) {
        result = result.concat(this.flattenUnitsTree(item.units, level + 1));
      }

      return result;
    }, []);
  }
  getAssignButtonName() {
    const { t } = this.props;
    return t('Assign');
  }

  handleUnitFilterChange = value => {
    this.setState(
      {
        selectedUnit: value,
        dataState: {
          ...this.state.dataState,
          skip: 0
        }
      },
      () => {
        this.queryOData();
      }
    );
  };

  render() {
    const {
      t,
      hasUserPermission,
      withVisibleColumns: { getVisibleColumnCount, openDialog, getGridColumns },
      isLoading,
      currentExercise
    } = this.props;

    const selectedServiceRequests = this.getSelectedServiceRequestIds();

    return (
      <div className="annex-overview">
        <ServiceRequestNavContext.Consumer>
          {nav => {
            this.setSavedDataState(nav);
            this.navigateToSavedLocation(nav);
          }}
        </ServiceRequestNavContext.Consumer>
        <ServiceRequestNavContext.Consumer>
          {nav =>
            nav.serviceRequestNavigation !==
              SERVICE_REQUEST_NAVIGATION_ORIGIN.AnnexOverview &&
            nav.setServiceRequestNavigation(
              SERVICE_REQUEST_NAVIGATION_ORIGIN.AnnexOverview
            )
          }
        </ServiceRequestNavContext.Consumer>
        <div className="annex-overview--heading">
          {this.renderInvoiceCreateDialog()}
          {this.renderInvoiceDialog(
            this.props.hasUserPermission(USER_ROLES.READONLY),
            this.queryOData,
            this.isInvoiceDialogOpen(),
            this.closeInvoiceDialog
          )}
          {this.assignDialog()}

          <H1>{t('Annex overview')}</H1>

          <Summary info={this.getSummaryInfo()} />

          <div className="annex-overview--grid-settings">
            <div className="annex-overview--heading-left">
              {this.props.withQuickSearchForm({
                onSearch: this.onQuickSearch,
                preFilledQuickSearch: this.state.preFilledQuickSearch
              })}

              {hasUserPermission(USER_ROLES.HOST_UNIT) && (
                <>
                  {this.HUOverview(this.onHuChange, this.state.showAllRequests)}
                </>
              )}
            </div>
            <div className="annex-overview--heading-actions">
              {this.UnitsFilter()}

              {!hasUserPermission(USER_ROLES.READONLY) && (
                <>
                  <RaisedButton
                    disabled={this.state.showAllRequests}
                    type="button"
                    onClick={this.openDialog}
                  >
                    {this.getAssignButtonName()}
                  </RaisedButton>
                  <RaisedButton
                    type="button"
                    disabled={this.state.showAllRequests}
                    onClick={this.openInvoiceDialog}
                  >
                    {t('Invoice')}
                  </RaisedButton>
                </>
              )}
              <GhostButton
                onClick={() => {
                  openDialog();
                }}
                type="button"
              >
                {t('Visible columns')} ({getVisibleColumnCount()})
              </GhostButton>
              <GhostButton
                disabled={this.getDisplayData().length === 0}
                iconLeft={<Icon icon="download" />}
                onClick={this.exportServiceRequests}
              >
                {t('XLSX')}
              </GhostButton>
            </div>
          </div>
        </div>
        <div>
          {this.props.withAnnexFilterForm((id, annex) => {
            const { selectedAnnex, preselectedAnnex } = this.state;

            // Additional condition to allow keep page then we are returning
            // from annex after it's preview.
            const shouldSetSkip =
              preselectedAnnex &&
              selectedAnnex &&
              selectedAnnex.annexId === preselectedAnnex.annexId;
            const additionalDataStateOverride = {};

            if (shouldSetSkip) {
              additionalDataStateOverride.skip = 0;
            }
            // End of condition;

            this.setState(
              {
                selectedAnnex: annex == selectedAnnex ? null : annex,
                preselectedAnnex: null,
                dataState: {
                  ...this.state.dataState,
                  ...additionalDataStateOverride
                }
              },
              this.queryOData
            );
          }, this.state.preselectedAnnex)}
        </div>
        {Object.keys(getGridColumns).length > 0 && (
          <>
            <ExcelExport
              ref={this.exportRef}
              data={this.state.XLSoData}
              fileName={ANNEX_OVERVIEW_EXPORT_FILE_NAME}
            >
              {Object.keys(getGridColumns).reduce((results, key) => {
                const column = getGridColumns[key];
                if (
                  (typeof column.xls === 'undefined' || column.xls) &&
                  column.visible
                ) {
                  if (column.filter === 'date') {
                    results.push(
                      <ExcelExportColumn
                        key={ShortId.generate()}
                        width={125}
                        field={column.xlsField || key}
                        title={column.title}
                      />
                    );
                  } else {
                    results.push(
                      <ExcelExportColumn
                        key={ShortId.generate()}
                        field={column.xlsField || key}
                        title={column.title}
                      />
                    );
                  }
                }
                return results;
              }, [])}
              {this.getExcelAdditionalColumns(getGridColumns)}
            </ExcelExport>
            <ServiceRequestNavContext.Consumer>
              {nav => (
                <Grid
                  key={this.state.gridKey}
                  ref={this.grid}
                  sortable={true}
                  resizable={true}
                  selectedField={
                    this.state.showAllRequests ? null : 'selectedField'
                  }
                  onHeaderSelectionChange={() =>
                    this.handleSelectAll(this.props.oData.data)
                  }
                  onSelectionChange={({ dataItem }) => {
                    this.handleSelect(dataItem);
                  }}
                  selectable={true}
                  reorderable={true}
                  pageable={{
                    pageSizes: PAGE_SIZES
                  }}
                  filter={DEFAULT_STATUS_FILTER}
                  {...this.state.dataState}
                  total={this.props.oData.total}
                  data={this.getDisplayData()}
                  onDataStateChange={this.dataStateChange}
                  cellRender={cell => {
                    if (this.state.showAllRequests) {
                      return {
                        ...cell,
                        props: {
                          ...cell.props,
                          children: ''
                        }
                      };
                    }
                    return cell;
                  }}
                  rowRender={(row, props) => {
                    if (props.dataItem.footer) {
                      return this.getGridFooter(
                        getVisibleColumnCount,
                        getGridColumns,
                        props.dataItem,
                        currentExercise
                      );
                    }

                    const indications = this.getIndications(
                      props.dataItem.Indications
                    );

                    return {
                      ...row,
                      props: {
                        ...row.props,
                        className: classnames(row.props.className, {
                          'annex-overview--highlighted': indications.length > 0,
                          'annex-overview--disabled':
                            props.dataItem.StatusId === 2,
                          'annex-overview--last-visited':
                            props.dataItem.ServiceRequestId ===
                            nav.serviceRequestId,
                          'annex-overview--host-unit-row':
                            !props.dataItem.CanHostUnitEdit &&
                            this.state.showAllRequests
                        })
                      }
                    };
                  }}
                >
                  <GridColumn
                    field="selectedField"
                    headerCell={this.state.showAllRequests ? 'empty' : null}
                    headerSelectionValue={
                      this.state.isAllSelected &&
                      selectedServiceRequests.length === 0
                    }
                    width="30px"
                  />

                  {this.getColumns()}
                </Grid>
              )}
            </ServiceRequestNavContext.Consumer>
          </>
        )}
        {isLoading && <GridLoader />}
      </div>
    );
  }

  repeatWindowScroll() {
    this.windowScrollInterval = setInterval(() => {
      this.scrollWindow();
      this.clearSavedOffset();
      if (!this.state.previousWindowScrollOffset) {
        clearInterval(this.windowScrollInterval);
      }
    }, 100);

    // just in case
    setTimeout(() => {
      if (this.windowScrollInterval) {
        clearInterval(this.windowScrollInterval);
      }
    }, 5000);
  }

  getExcelAdditionalColumns(getGridColumns) {
    const { t } = this.props;
    const result = [];

    if (
      getGridColumns.ActualGrossAmount &&
      getGridColumns.ActualGrossAmount.visible
    ) {
      result.push(
        <ExcelExportColumn
          key={ShortId.generate()}
          field="ActualGrossAmount"
          title={t('Actual gross amount')}
          footer={() => {
            return (
              t('Sum: ') + this.getXlsTotals().FooterTotalActualGrossAmount
            );
          }}
        />
      );
    }

    if (getGridColumns.ActualCost && getGridColumns.ActualCost.visible) {
      result.push(
        <ExcelExportColumn
          key={ShortId.generate()}
          field="ActualCost"
          title={t('Actual cost')}
          footer={() => {
            return t('Sum: ') + this.getXlsTotals().FooterTotalActualCost;
          }}
        />
      );
    }

    if (
      getGridColumns.EstimatedTotalCost &&
      getGridColumns.EstimatedTotalCost.visible
    ) {
      result.push(
        <ExcelExportColumn
          key={ShortId.generate()}
          field="EstimatedTotalCostFrom"
          title={t('Total cost from')}
          footer={() => {
            return (
              t('Sum: ') + this.getXlsTotals().FooterTotalEstimatedCostFrom
            );
          }}
        />
      );
      result.push(
        <ExcelExportColumn
          key={ShortId.generate()}
          field="EstimatedTotalCostTo"
          title={t('Total cost to')}
          footer={() => {
            return t('Sum: ') + this.getXlsTotals().FooterTotalEstimatedCostTo;
          }}
        />
      );
    }

    if (
      getGridColumns.EstimatedGrossAmount &&
      getGridColumns.EstimatedGrossAmount.visible
    ) {
      result.push(
        <ExcelExportColumn
          key={ShortId.generate()}
          field="EstimatedGrossAmountFrom"
          title={t('Total gross amount from')}
          footer={() => {
            return (
              t('Sum: ') +
              this.getXlsTotals().FooterTotalEstimatedGrossAmountFrom
            );
          }}
        />
      );
      result.push(
        <ExcelExportColumn
          key={ShortId.generate()}
          field="EstimatedGrossAmountTo"
          title={t('Total gross amount to')}
          footer={() => {
            return (
              t('Sum: ') + this.getXlsTotals().FooterTotalEstimatedGrossAmountTo
            );
          }}
        />
      );
    }

    return result;
  }

  getGridFooter(
    getVisibleColumnCount,
    getGridColumns,
    dataItem,
    currentExercise
  ) {
    const { t } = this.props;

    return (
      <tr className="k-master-row">
        <td colSpan={getVisibleColumnCount() + 1}>
          <div className="annex-overview--footer">
            <div className="annex-overview--footer--title">
              {t('Sum')}
              {` [${currentExercise.currency.name}]`}:
            </div>
            <div className="annex-overview--footer--items">
              {getGridColumns.ActualCost && getGridColumns.ActualCost.visible && (
                <div className="annex-overview--footer--item">
                  <Tooltip tooltip={t('Actual cost')} position="top">
                    <>
                      <Money amount={dataItem.FooterTotalActualCost} />
                    </>
                  </Tooltip>
                </div>
              )}
              {getGridColumns.ActualGrossAmount &&
                getGridColumns.ActualGrossAmount.visible && (
                  <div className="annex-overview--footer--item">
                    <Tooltip
                      tooltip={t('Total Actual gross amount')}
                      position="top"
                    >
                      <>
                        <Money amount={dataItem.FooterTotalActualGrossAmount} />
                      </>
                    </Tooltip>
                  </div>
                )}
              <div className="annex-overview--footer--item">
                <Tooltip tooltip={t('Total cost')} position="top">
                  <>
                    <Money amount={dataItem.FooterTotalEstimatedCostFrom} />

                    {dataItem.FooterTotalEstimatedCostFrom !==
                      dataItem.FooterTotalEstimatedCostTo && (
                      <>
                        {' - '}
                        <Money amount={dataItem.FooterTotalEstimatedCostTo} />
                      </>
                    )}
                  </>
                </Tooltip>
              </div>
              <div className="annex-overview--footer--item">
                <Tooltip tooltip={t('Total gross amount')} position="top">
                  <>
                    <Money
                      amount={dataItem.FooterTotalEstimatedGrossAmountFrom}
                    />

                    {dataItem.FooterTotalEstimatedGrossAmountFrom !==
                      dataItem.FooterTotalEstimatedGrossAmountTo && (
                      <>
                        {' - '}
                        <Money
                          amount={dataItem.FooterTotalEstimatedGrossAmountTo}
                        />
                      </>
                    )}
                  </>
                </Tooltip>
              </div>
            </div>
          </div>
        </td>
      </tr>
    );
  }
}

AnnexOverview.propTypes = {
  isLoading: PropTypes.bool,
  withQuickSearchFilter: PropTypes.object,
  withAnnexFilterSelected: PropTypes.string,
  withAnnexFilterOdata: PropTypes.func,
  withAnnexFilterForm: PropTypes.func,
  getAnnexes: PropTypes.func,
  oData: PropTypes.object,
  withVisibleColumns: PropTypes.object,
  units: PropTypes.array,
  huUnits: PropTypes.array,
  queryOData: PropTypes.func,
  queryXLSOData: PropTypes.func,
  resetOData: PropTypes.func,
  withQuickSearchForm: PropTypes.func,
  t: PropTypes.func,
  match: PropTypes.object,
  location: PropTypes.object,
  history: PropTypes.object,
  currentExercise: PropTypes.object,
  hasUserPermission: PropTypes.func,
  getAnnexOverviewGrid: PropTypes.func,
  annexOverviewGrid: PropTypes.object,
  bulkAssignForSubmit: PropTypes.func,
  bulkAssign: PropTypes.func,
  bulkAppoint: PropTypes.func,
  canBulkAssign: PropTypes.func,
  subcontractors: PropTypes.array,
  getAnnexFilteredServiceRequestIds: PropTypes.func,
  getNotifications: PropTypes.func
};

export default AnnexOverview;
